/**
 * Exam simulation type definitions.
 *
 * Defines TypeScript types and interfaces for exam simulation functionality,
 * including exam sections, questions, paragraphs, exam state, and question
 * types (multiple choice, fill-in-the-blank, speaking).
 *
 * @module exam
 */

export type ExamSection = 'reading' | 'writing' | 'listening' | 'speaking';

export interface Paragraph {
  id: number;
  content: string;
}

export interface AnswerOption {
  id: string;
  text: string;
}

export interface Question {
  id: number;
  stem: string;
  options: AnswerOption[];
  paragraphRef: number; // Which paragraph this question refers to
}

export interface TextChunk {
  id: number;
  title: string;
  paragraphs: Paragraph[];
  questionIds: number[];
}

export interface ExamState {
  currentChunkIndex: number;
  activeQuestionId: number | null;
  selectedAnswers: Record<number, string>;
  struckAnswers: Record<number, string[]>;
  flaggedQuestions: number[];
  highlights: Record<number, { start: number; end: number }[]>;
  timeRemaining: number;
  textSize: 'small' | 'medium' | 'large';
  highlighterActive: boolean;
}

export interface SpeakingContextAudio {
  url?: string | null;
  binary?: string | null;
  mimeType?: string | null;
}

export interface SpeakingContextImage {
  url?: string | null;
  binary?: string | null;
  mimeType?: string | null;
}

export interface SpeakingQuestion {
  id: string; // Backend UUID
  displayId: number; // Sequential number for UI
  prompt: string;
  adaptiveState?: 'new' | 'retry' | 'mastery-check';
  image?: string | null; // Optional image URL or path
  contextText?: string | null;
  prepTime?: number; // Preparation time in seconds
  speakTime?: number; // Speaking time in seconds
  contextAudio?: SpeakingContextAudio | null;
  contextImage?: SpeakingContextImage | null;
  section?: number;
  /** Backend question type. 'live-conversation' routes to the AI-led dialogue flow; all other values use prep → recording. */
  type?: string | null;
  /** Rubric task type (e.g. 'dialogue_roleplay_discussion'); used for grading/scoring only, not for UI routing. */
  taskType?: string | null;
  /** True when this question should run the AI-led live-conversation flow (LCT-001). */
  isLiveConversation?: boolean;
}

/**
 * The dedicated question `type` value that routes to the live-conversation flow.
 * `LIVE_CONVERSATION_TASK_TYPE` is retained for rubric/grading use only — it is
 * NOT used for UI routing.
 */
export const LIVE_CONVERSATION_TASK_TYPE = 'dialogue_roleplay_discussion';
export const LIVE_CONVERSATION_QUESTION_TYPE = 'live-conversation';

/** Whether a question should run the AI-led live-conversation flow (LCT-001).
 *  Routing is determined solely by `type === 'live-conversation'`.
 */
export function isLiveConversationTask(
  meta: { type?: string | null; taskType?: string | null } | null | undefined,
): boolean {
  if (!meta) return false;
  return meta.type === LIVE_CONVERSATION_QUESTION_TYPE;
}
